import React from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Drawer,
  IconButton,
  List,
  ListItem,
  Stack,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { deviceCommentsApi } from '../../api/services';
import { isKioskMode } from '../../utils/kioskMode';

const MAX_BODY_LENGTH = 2000;

export interface CommentDrawerProps {
  deviceId: number | null;
  deviceLabel: string | null;
  onClose: () => void;
  /** Called after a successful append so the parent can refresh tile counts. */
  onAppended?: () => void;
}

function formatTs(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

export const CommentDrawer: React.FC<CommentDrawerProps> = ({
  deviceId,
  deviceLabel,
  onClose,
  onAppended,
}) => {
  const open = deviceId !== null;
  const queryClient = useQueryClient();
  const [draft, setDraft] = React.useState('');

  const { data: comments, isLoading, isError, refetch } = useQuery({
    queryKey: ['device-comments', deviceId],
    queryFn: () => deviceCommentsApi.list(deviceId as number),
    enabled: deviceId !== null,
    // Stay aligned with whoever else might be appending; cheap fetch.
    staleTime: 0,
  });

  const appendMutation = useMutation({
    mutationFn: ({ id, body }: { id: number; body: string }) => deviceCommentsApi.append(id, body),
    onSuccess: () => {
      setDraft('');
      if (deviceId !== null) {
        queryClient.invalidateQueries({ queryKey: ['device-comments', deviceId] });
      }
      onAppended?.();
    },
  });

  // Reset the draft when switching devices.
  React.useEffect(() => {
    setDraft('');
    appendMutation.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId]);

  const trimmedLength = draft.trim().length;
  const overLimit = draft.length > MAX_BODY_LENGTH;
  const canSubmit = deviceId !== null && trimmedLength > 0 && !overLimit && !appendMutation.isPending;

  const handleSubmit = () => {
    if (!canSubmit || deviceId === null) return;
    appendMutation.mutate({ id: deviceId, body: draft });
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100vw', sm: 420 } } }}
    >
      {/* Spacer below the fixed AppBar so the drawer header isn't covered.
          Kiosk mode strips the AppBar entirely (Layout.tsx), so skip the spacer there. */}
      {!isKioskMode() && <Toolbar />}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Device comments
          </Typography>
          {deviceLabel && (
            <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
              {deviceLabel}
            </Typography>
          )}
        </Box>
        <IconButton onClick={onClose} size="small" aria-label="Close comments drawer">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>

      <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2 }}>
        {isLoading && (
          <Stack alignItems="center" sx={{ py: 4 }}>
            <CircularProgress size={24} />
          </Stack>
        )}
        {isError && (
          <Alert severity="error" action={<Button onClick={() => refetch()}>Retry</Button>}>
            Failed to load comments.
          </Alert>
        )}
        {!isLoading && !isError && comments && comments.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No comments yet. Add the first one below — comments live with the device for institutional memory.
          </Typography>
        )}
        {!isLoading && !isError && comments && comments.length > 0 && (
          <List disablePadding>
            {comments.map((c, idx) => (
              <ListItem key={`${c.ts}-${idx}`} alignItems="flex-start" disableGutters sx={{ flexDirection: 'column', alignItems: 'stretch', mb: 1.5 }}>
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.25 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    {c.author}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {formatTs(c.ts)}
                  </Typography>
                </Stack>
                {/* Plain-text rendering only — no dangerouslySetInnerHTML, no markdown
                    library. React's text-node escaping is the rendering policy. */}
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {c.body}
                </Typography>
              </ListItem>
            ))}
          </List>
        )}
      </Box>

      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <TextField
          multiline
          fullWidth
          minRows={3}
          maxRows={8}
          placeholder="Append a note…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          error={overLimit}
          helperText={`${draft.length} / ${MAX_BODY_LENGTH}`}
          inputProps={{ maxLength: MAX_BODY_LENGTH + 1 }}
          disabled={deviceId === null || appendMutation.isPending}
        />
        {appendMutation.isError && (
          <Alert severity="error" sx={{ mt: 1 }}>Failed to append comment.</Alert>
        )}
        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {appendMutation.isPending ? 'Posting…' : 'Post comment'}
          </Button>
        </Stack>
      </Box>
    </Drawer>
  );
};
